"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildApptPurposesFromGxApptPurposes = exports.isOrderBasedScheduling = exports.getPatientVisitMethod = exports.getPatientRelationship = exports.getAppointmentDate = exports.isBackToSearchBtnRendered = exports.shouldSkipGX = exports.getBackToSearchPageUrl = exports.isValidPatientRegForm = exports.relatedFailedStateMatchHelper = exports.isValidEHRAddress = exports.isValidEHRGender = exports.isValidEHRDOB = exports.isValidEHRName = exports.getNextStepId = exports.getStepIndex = exports.hidePatientRel = exports.getVisitTypeTitle = exports.getApptSlotId = exports.hasAnyVideoPhoneAppt = exports.formatApiPhone = exports.getPhoneEhrPatientDemographics = exports.toTitleCase = exports.trimStringProperties = exports.makeNameString = exports.formatLastApptShownDate = exports.formatApptTime = exports.formatHeaderDate = exports.momentAtMidnight = exports.formatApiDate = exports.getPurposeOptionsByPatientRel = exports.getProviderImageUrl = exports.buildPurposesSetFromResponses = exports.buildMapUrl = exports.getApptLocation = exports.getProviderContactNumber = exports.getMainContactNumber = exports.getProviderLocationStateFullName = exports.formatLocation = exports.formatProviderSpecialties = exports.formatProviderName = exports.formatProviderDegrees = exports.formatProviderSecialties = void 0;
const moment_1 = __importDefault(require("moment"));
const react_phone_number_input_1 = require("react-phone-number-input");
const isPlainObject_1 = __importDefault(require("lodash/isPlainObject"));
const isEmpty_1 = __importDefault(require("lodash/isEmpty"));
const images_1 = require("./constants/images");
const VisitTypes_1 = require("./constants/VisitTypes");
const provider_1 = require("./constants/provider");
const Filters_1 = require("./constants/Filters");
const steps_1 = __importDefault(require("./constants/steps"));
const States_1 = require("./constants/States");
const providerStates_1 = require("./constants/providerStates");
const Visibility_1 = require("./constants/Visibility");
const messages_1 = __importDefault(require("./messages"));
const formatProviderSecialties = (specialties = []) => specialties
    .filter((v) => !!v.specialty)
    .map((v) => v.specialty)
    .join(', ');
exports.formatProviderSecialties = formatProviderSecialties;
const formatProviderDegrees = (degrees = []) => degrees
    .filter(({ name }) => !!name)
    .map(({ name }) => name)
    .join(',');
exports.formatProviderDegrees = formatProviderDegrees;
const formatProviderName = ({ name, degrees }) => {
    const formattedDegrees = (0, exports.formatProviderDegrees)(degrees);
    // account for both v8 and v9 data while in transition (appt-cancel flow hits v8)
    // TODO: remove the fallback logic when both flows use search v9
    const fullName = (name === null || name === void 0 ? void 0 : name.full) || (name === null || name === void 0 ? void 0 : name.full_name);
    return [fullName, formattedDegrees ? ` ${formattedDegrees}` : null]
        .filter((v) => !!v)
        .join();
};
exports.formatProviderName = formatProviderName;
const formatProviderSpecialties = (specialties) => specialties
    .filter((specialty) => !!specialty.name)
    .map((specialty) => specialty.name)
    .join(', ');
exports.formatProviderSpecialties = formatProviderSpecialties;
const formatLocation = ({ street1, street2, suite, city, state, zip }) => {
    // Combine street address components and city with commas
    const streetAndCityAddress = [street1, street2, suite, city]
        .filter((component) => !!component)
        .join(', ');
    // Combine state and zip with a space
    const stateAndZipCode = [state, zip]
        .filter((component) => !!component)
        .join(' ');
    // Create the complete formatted address
    const formattedAddress = [streetAndCityAddress, stateAndZipCode]
        .filter((component) => !!component)
        .join(', ');
    return formattedAddress;
};
exports.formatLocation = formatLocation;
const insertOrConjunctionBeforeLastElem = (elem, i, array) => i != array.length - 1 ? elem : 'or ' + elem;
/**
 * Finds a provider's practice location
 * state (or territory) abbreviation, and returns the full name.
 * Use case: During pre-booking validation, a patient seeking a virtual visit
 * may be asked for telehealth attestation.
 *   - e.g., "Will you be in [[provider_state]] during the appointment?"
 *
 * @param {Object} provider
 * @returns {string} human-readable string composed of all full names of states (or territories), separated by commas and/or "or", or default to value of location.state
 */
const getProviderLocationStateFullName = ({ locations }) => {
    /**`state` values here will be abbreviated names, e.g. "OH" for "Ohio" */
    const uniqueNames = [...new Set(locations.map(({ state }) => state))];
    const names = uniqueNames.map((name) => { var _a; return (_a = providerStates_1.PROVIDER_STATES[name]) !== null && _a !== void 0 ? _a : name; });
    if (names.length < 2)
        return names.join();
    if (names.length == 2)
        return names.join(' or ');
    return names.map(insertOrConjunctionBeforeLastElem).join(', ');
};
exports.getProviderLocationStateFullName = getProviderLocationStateFullName;
/**
 * Based on a provider and maybe* appointment, returns the correct contact number for that provider
 * If an appointment is passed in, we'll return the contact for that appointment slot location
 * @param {Object} provider
 * @param {Object} appointment
 * @returns {string}
 */
const getMainContactNumber = (provider, appointment = null) => {
    if (appointment) {
        return (0, exports.getApptLocation)(provider, appointment).phone;
    }
    const providerPhone = (0, exports.getProviderContactNumber)(provider);
    if (providerPhone) {
        return providerPhone;
    }
    return provider.locations[0] ? provider.locations[0].phone : '';
};
exports.getMainContactNumber = getMainContactNumber;
/**
 * Returns the contact number for a given provider
 * @param {Object} provider
 * @returns {String}
 */
const getProviderContactNumber = (provider) => {
    const { contacts = [] } = provider;
    const { value } = contacts.find((val) => val.contact_type === 'phone') || {};
    return value;
};
exports.getProviderContactNumber = getProviderContactNumber;
/**
 *
 * @param {Object} provider
 * @param {Object} appointment
 * @returns {Object} provider location object that is related to the appointment
 */
const getApptLocation = (provider, appointment) => {
    return provider.locations.find((location) => {
        var _a;
        return (appointment &&
            location.id.toString() === ((_a = appointment === null || appointment === void 0 ? void 0 : appointment.location_id) === null || _a === void 0 ? void 0 : _a.toString()));
    });
};
exports.getApptLocation = getApptLocation;
// Within an app, a mapApiKey wouldn't be passed down because we would already
// have googleMaps js loaded in the header.
// This accounts for usage within storybook
const buildMapUrl = (apiKey) => `https://maps.googleapis.com/maps/api/js?key=${apiKey}&amp;language=en&amp;region=US&amp;libraries=places`;
exports.buildMapUrl = buildMapUrl;
/**
 * ⚠️⚠️ This function is exported to be used outside of this package ⚠️⚠️
 * Given respones from the slots api for both established/new purposes, build an object
 * keyed by relationship type of Sets of appointment types
 * @param {} object containing `establishedPurposesResponse` and `newPurposesResponse`
 * @returns
 */
const buildPurposesSetFromResponses = ({ establishedPurposesResponse, newPurposesResponse }) => {
    return {
        [Filters_1.ESTABLISHED_PATIENT]: new Set(establishedPurposesResponse.facets.length > 0
            ? establishedPurposesResponse.facets[0].terms.map((term) => term.value)
            : []),
        [Filters_1.NEW_PATIENT]: new Set(newPurposesResponse.facets.length > 0
            ? newPurposesResponse.facets[0].terms.map((term) => term.value)
            : [])
    };
};
exports.buildPurposesSetFromResponses = buildPurposesSetFromResponses;
/**
 * Given a provider, return the provider image or a placeholder based on the
 * provider's gender
 * @param {Object} provider
 */
const getProviderImageUrl = (provider) => {
    const { image_url, gender } = provider || {};
    if (image_url) {
        return image_url;
    }
    else if (gender) {
        const lcGender = gender.toLowerCase();
        if (lcGender === 'male') {
            return images_1.MALE_PROVIDER_AVATAR_URL;
        }
        else if (lcGender === 'female') {
            return images_1.FEMALE_PROVIDER_AVATAR_URL;
        }
    }
    return images_1.NEUTRAL_PROVIDER_AVATAR_URL;
};
exports.getProviderImageUrl = getProviderImageUrl;
/**
 * Given a provider, will return a list of purpose options by patientRel
 * @param {Object} provider
 * @returns {Object} {new: Set(), established: Set()}
 */
const getPurposeOptionsByPatientRel = (provider, visibility = Visibility_1.SCHEDULING_VISIBILITY_CONSUMER) => {
    const purposeByRel = {
        new: {},
        established: {}
    };
    const [visKey, visValue] = visibility.split(':');
    const ehrPurposes = provider.appointment_ehr_purposes || [];
    ehrPurposes.forEach(({ ehr_data, name, patient_relationship }) => {
        const visiblePurpose = ehr_data.find(({ visibilities }) => {
            return visibilities[visKey] === visValue;
        });
        if (visiblePurpose) {
            const { visit_method } = visiblePurpose;
            purposeByRel[patient_relationship][`${name}${visit_method}`] = {
                name,
                visit_method
            };
        }
    });
    const sortPurposes = (purposeByRel) => Object.values(purposeByRel).sort((a, b) => a.name.localeCompare(b.name));
    return {
        new: sortPurposes(purposeByRel.new),
        established: sortPurposes(purposeByRel.established)
    };
};
exports.getPurposeOptionsByPatientRel = getPurposeOptionsByPatientRel;
/**
 * Formats the given date to something expected by api
 * @param {Date | moment} date - anything usable by `moment` (date, string, momentDate)
 * @returns {string}
 */
const formatApiDate = (date) => (0, moment_1.default)(date).format('YYYY-MM-DD HH:mm:00');
exports.formatApiDate = formatApiDate;
/**
 * Returns a date like the one passed in with the time portion set to midnight
 * @param {Date} date - anything parseable by the `moment` constructor: ie Date|String|another moment
 * @returns {Date} date adjusted to midnight
 */
const momentAtMidnight = (date) => {
    return (0, moment_1.default)(date).startOf('day');
};
exports.momentAtMidnight = momentAtMidnight;
/**
 * Formats the given date to 'dddd, MMMM DD, YYYY'
 * @param {Date | moment | string} date - anything usable by `moment` (date, string, momentDate)
 * @returns {string}
 */
const formatHeaderDate = (date) => (0, moment_1.default)(date).format('dddd, MMMM DD, YYYY');
exports.formatHeaderDate = formatHeaderDate;
/**
 * Formats the given date to 'h:mm A'
 * @param {Date | moment | string} date - anything usable by `moment` (date, string, momentDate)
 * @returns {string}
 */
const formatApptTime = (date) => (0, moment_1.default)(date).format('h:mm A');
exports.formatApptTime = formatApptTime;
/**
 * Formats the given date to 'MMMM DD, YYYY'
 * @param {Date | moment} date - anything usable by `moment` (date, string, momentDate)
 * @returns {string}
 */
const formatLastApptShownDate = (date) => (0, moment_1.default)(date).format('MMMM DD, YYYY');
exports.formatLastApptShownDate = formatLastApptShownDate;
/**
 * Concatenates first, middle, and last into a single name string
 * @param {object} name An object containing first, middle, and last names
 * @param {string} name.first First name
 * @param {string} name.middle Middle name
 * @param {string} name.last Last name
 *
 * @returns {string} name (first middle last)
 */
const makeNameString = ({ first, middle, last }) => {
    const name = [];
    if (first)
        name.push(first);
    if (middle)
        name.push(middle);
    if (last)
        name.push(last);
    return name.join(' ');
};
exports.makeNameString = makeNameString;
/**
 * Shallowly trim string properties within a plain object. Properties that are not strings are not mutated.
 * @param {object} plainObj An object containing string properties (e.g. { first: "  Dwayne  ", middle: " (The Rock) ", last: " Johnson    " })
 * @returns {object} Trimmed string properties (e.g. { first: "Dwayne", middle: "(The Rock)", last: "Johnson" })
 */
const trimStringProperties = (plainObj) => {
    if (plainObj == null || (0, isPlainObject_1.default)(plainObj) === false)
        return plainObj;
    const newPlainObj = {};
    Object.keys(plainObj).forEach((key) => {
        const value = plainObj[key];
        const type = typeof value;
        if (type === 'string') {
            newPlainObj[key] = value.trim();
        }
        else {
            newPlainObj[key] = value;
        }
    });
    return newPlainObj;
};
exports.trimStringProperties = trimStringProperties;
/**
 * Capitalizes a proper noun or phrase, first letter capital with the
 * rest lowercased for each word, with special handling for names with
 * special characters such as hyphens and apostrophes
 * @param {string} word
 */
const toTitleCase = (word) => {
    if (typeof word !== 'string') {
        return '';
    }
    if (word.length <= 1) {
        return word.toUpperCase();
    }
    /**
     * If a special character is encountered, both left/right
     * sides of the special character will be title-cased independently
     * for examples
     * o'neil => O'Neil
     * the bird => The Bird
     * john-paul => John-Paul
     */
    const splitOnCharacters = ["'", '’', ' ', '-', '–'];
    for (let i = 0; i < splitOnCharacters.length; i++) {
        const char = splitOnCharacters[i];
        if (word.indexOf(char) > -1) {
            // break up the string into substrings, recursively call toTitleCase
            // on each and THEN join the results
            return word.split(char).map(exports.toTitleCase).join(char);
        }
    }
    return word.charAt(0).toUpperCase() + word.substr(1).toLowerCase();
};
exports.toTitleCase = toTitleCase;
/**
 * Returns an E.164 formatted phone number
 * @param {string?} phoneNumber phone number
 * @returns {string} E.164 formatted phone number
 */
const formatPhoneNumberAsE164 = (phoneNumber = '') => {
    // Remove any & all hyphens from phone number
    const strNum = phoneNumber.replace(/-/g, '');
    // if number is locally formatted (without location code),
    // should add location code +1 for US/CA
    const shouldAddLocationCode = strNum.indexOf('+') !== 0 && strNum.length === 10;
    return shouldAddLocationCode ? `+1${strNum}` : strNum;
};
/**
 * Returns an object with:
 * - a E.164 formatted phone number (required because the phone-number input operates on phone numbers in E.164 format) and
 * - a phone type
 * 1. If defaultPhoneType exists, the returned object's type will be the defaultPhoneType and we'll try
 *    to find a corresponding number from the EhrPatientDemographics' phones (otherwise empty string)
 * 2. If defaultPhoneType does not exist, we'll check if a phone with mobile type exists and return it
 * 3. Otherwise, we'll return an object with the first phone's number. Not returning type here b/c when
 *    the provided type (e.g. type: "unknown") does not exist in CS config's type options array, forms-v2
 *    preselects the first type from config which is inaccurate
 * @param {array} phones an array of phones objects in the shape { type, number }
 * @param {Object} phoneConfig customer service forms-v2 config for the phone field
 * @returns {Object} {type?: string, number: string}
 */
const getPhoneEhrPatientDemographics = (phones = [], phoneConfig) => {
    var _a, _b, _c;
    const defaultPhoneType = (_b = (_a = phoneConfig === null || phoneConfig === void 0 ? void 0 : phoneConfig.subFields) === null || _a === void 0 ? void 0 : _a.type) === null || _b === void 0 ? void 0 : _b.defaultValue;
    if (defaultPhoneType) {
        const phone = phones.find((phone) => phone.type === defaultPhoneType) || {};
        const number = phone.number || '';
        return { type: defaultPhoneType, number: formatPhoneNumberAsE164(number) };
    }
    const mobilePhone = phones.find((phone) => phone.type === 'mobile') || {};
    if (!(0, isEmpty_1.default)(mobilePhone)) {
        const number = mobilePhone.number || '';
        return { type: 'mobile', number: formatPhoneNumberAsE164(number) };
    }
    return {
        number: formatPhoneNumberAsE164((_c = phones[0]) === null || _c === void 0 ? void 0 : _c.number)
    };
};
exports.getPhoneEhrPatientDemographics = getPhoneEhrPatientDemographics;
/**
 * Returns a phone number formatted for the API, which is a local
 * number without country code or formatting
 * @param {string} phoneNumber phone number in E.164 format
 * @returns {string} phoneNumber formatted locally, integers only
 */
const formatApiPhone = (phoneNumber) => {
    const parsed = (0, react_phone_number_input_1.parsePhoneNumber)(phoneNumber);
    if (parsed) {
        // if phone parsed as valid, return its local version
        return parsed.nationalNumber;
    }
    // if phone was invalid, return what came in stripped of formatting
    return (phoneNumber || '').replace(/\D*/g, '');
};
exports.formatApiPhone = formatApiPhone;
const hasAnyVideoPhoneAppt = (query) => {
    return [VisitTypes_1.PHONE, VisitTypes_1.VIDEO].includes(query);
};
exports.hasAnyVideoPhoneAppt = hasAnyVideoPhoneAppt;
const getApptSlotId = (appointment) => {
    return `slot-${appointment.location_id.toString()}-${appointment.start_datetime.replace(/\s+/g, '')}`;
};
exports.getApptSlotId = getApptSlotId;
const getVisitTypeTitle = (type) => {
    const placeholder = {
        [VisitTypes_1.PHONE]: 'appointmentPhoneTitle',
        [VisitTypes_1.VIDEO]: 'appointmentVideoTitle',
        [VisitTypes_1.IN_PERSON]: 'appointmentInPersonTitle'
    };
    return placeholder[type];
};
exports.getVisitTypeTitle = getVisitTypeTitle;
/**
 * Determines whether or not we should hide the patient relationship question on the ApptInfo
 * step in the flow based on the provider.entity_type and a config flag (hide_patient_rel_for_locations)
 * @param {object} provider a provider or bookable entity (e.g. location/clinic)
 * @param {boolean} hidePatientRelForLocations config flag; true means hide the patient rel question
 * @param returns true if we should hide the patient rel question, else false
 */
const hidePatientRel = (provider, hidePatientRelForLocations) => {
    var _a, _b;
    if (provider[provider_1.ENTITY_TYPE] &&
        provider[provider_1.ENTITY_TYPE] === provider_1.LOCATION_ENTITY_TYPE &&
        hidePatientRelForLocations === true) {
        return true;
    }
    if ((_b = (_a = provider === null || provider === void 0 ? void 0 : provider.patient_rel_question) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.hidden) {
        // this value will come back to us from XP as a string
        // if any string is set, other than an empty string (which is false-y), the question will be hidden
        return true;
    }
    return false;
};
exports.hidePatientRel = hidePatientRel;
/**
 * Get numeric index that the given stepId has in the given stepsConfigId
 * @param {string} stepsConfigId see lib/constants/steps for config ids
 * @param {*} stepId Step ID from constants/states
 * @return {number} index of the step, or -1 if the step is not in the steps config
 */
const getStepIndex = (stepsConfigId, stepId) => {
    const steps = steps_1.default[stepsConfigId];
    return steps.findIndex((step) => step.stateKey === stepId);
};
exports.getStepIndex = getStepIndex;
/**
 * Get the step id of the step immediately following the provided step id.
 * @param {string} stepsConfigId see lib/constants/steps for config ids
 * @param {*} curStepId constants/states value for the 'current state'
 * @return {?number} stepId of the next step, or null if there is no next step
 */
const getNextStepId = (stepsConfigId, curStepId) => {
    const steps = steps_1.default[stepsConfigId];
    const currentStepIndex = (0, exports.getStepIndex)(stepsConfigId, curStepId);
    if (steps[currentStepIndex + 1]) {
        return steps[currentStepIndex + 1].stateKey;
    }
    return null;
};
exports.getNextStepId = getNextStepId;
/**
 * Helper function to determine whether a patient name is valid for EHR purposes.
 * Use idealized requirements of first and last being present and non-whitespace.
 * @param {Object} name An object representing a name, with properties for first, last, middle, etc.
 */
const isValidEHRName = (name = { first: '', last: '' }) => {
    var _a, _b;
    const firstValid = !!((_a = name === null || name === void 0 ? void 0 : name.first) === null || _a === void 0 ? void 0 : _a.trim());
    const lastValid = !!((_b = name === null || name === void 0 ? void 0 : name.last) === null || _b === void 0 ? void 0 : _b.trim());
    return firstValid && lastValid;
};
exports.isValidEHRName = isValidEHRName;
/**
 * Helper function to determine whether a patient dob is valid for EHR purposes.
 * Use idealized requirements of just being present and non-whitespace.
 * @param {string} dob A string representing a date of birth.
 */
const isValidEHRDOB = (dob = '') => {
    return dob && !!(dob === null || dob === void 0 ? void 0 : dob.trim());
};
exports.isValidEHRDOB = isValidEHRDOB;
/**
 * Helper function to determine whether a patient gender is valid for EHR purposes.
 * Use idealized requirements of just being present and non-whitespace.
 * @param {string} gender A string representing a gender.
 */
const isValidEHRGender = (gender = '') => {
    return gender && !!(gender === null || gender === void 0 ? void 0 : gender.trim());
};
exports.isValidEHRGender = isValidEHRGender;
/**
 * Helper function to determine whether a patient address is valid for EHR purposes.
 * Use idealized requirements of city, state, street1, and zip just being present and non-whitespace.
 * @param {Object} address An object representing an address, with properties for city, state, street1, street2, zip, etc.
 */
const isValidEHRAddress = (address = { city: '', state: '', street1: '', zip: '' }) => {
    var _a, _b, _c, _d;
    const cityValid = !!((_a = address === null || address === void 0 ? void 0 : address.city) === null || _a === void 0 ? void 0 : _a.trim());
    const stateValid = !!((_b = address === null || address === void 0 ? void 0 : address.state) === null || _b === void 0 ? void 0 : _b.trim());
    const street1Valid = !!((_c = address === null || address === void 0 ? void 0 : address.street1) === null || _c === void 0 ? void 0 : _c.trim());
    const zipValid = !!((_d = address === null || address === void 0 ? void 0 : address.zip) === null || _d === void 0 ? void 0 : _d.trim());
    return cityValid && stateValid && street1Valid && zipValid;
};
exports.isValidEHRAddress = isValidEHRAddress;
/*
 * Helper function to determine when the state machine is in one of the "related patient failure"
 * states (`ehrRelatedFailed` or `ehrSomebodyElseFormRelatedFailed`)
 *
 * @param state xstate state object
 * @returns boolean Whether the state machine is in a ehr related patient failure context
 */
const relatedFailedStateMatchHelper = (state) => {
    return (state &&
        (Object.values(state.value).includes(States_1.ehrFormRelatedFailed) ||
            Object.values(state.value).includes(States_1.ehrSomebodyElseFormRelatedFailed)));
};
exports.relatedFailedStateMatchHelper = relatedFailedStateMatchHelper;
/**
 * Util function that determines if the given object is a valid patient reg form
 * config. Doesn't do deep analysis, simply checks for a non-empty object.
 *
 * @param {Object} patientRegForm
 * @returns Boolean
 */
const isValidPatientRegForm = (patientRegForm) => {
    return (patientRegForm &&
        typeof patientRegForm === 'object' &&
        !Array.isArray(patientRegForm) &&
        Object.keys(patientRegForm).length > 0);
};
exports.isValidPatientRegForm = isValidPatientRegForm;
const getBackToSearchPageUrl = () => {
    const origin = window.location.origin;
    const params = new URLSearchParams(window.location.search);
    return params.toString() ? `${origin}/search?${params.toString()}` : null;
};
exports.getBackToSearchPageUrl = getBackToSearchPageUrl;
/**
 * Determines whether the 'skipGX' parameter is present in the URL search parameters.
 *
 * @returns {boolean} Returns true if the 'skipGX' parameter is present, otherwise false.
 */
const shouldSkipGX = () => {
    var _a;
    const params = new URLSearchParams(window.location.search);
    return (_a = params.get('skipGX')) !== null && _a !== void 0 ? _a : false;
};
exports.shouldSkipGX = shouldSkipGX;
/**
 * Checks whether back to search button should be rendered
 *
 * @param {*} param0
 * @param {string} param0.backToSearchPageURL
 * @param {boolean} param0.searchMatchEnabled
 *
 * @returns
 */
const isBackToSearchBtnRendered = ({ backToSearchPageURL, searchMatchEnabled }) => !!backToSearchPageURL && searchMatchEnabled;
exports.isBackToSearchBtnRendered = isBackToSearchBtnRendered;
/**
 * Formats the appointment date and time from the selected appointment.
 *
 * @param {Object} selectedAppointment - The selected appointment object.
 * @param {boolean} includeWeekOfday - Whether to include the day of the week in the formatted date.
 *
 * @returns {{
    formattedDate: string;
    formattedTime: string;
  }} An object containing the formatted date and time.
 */
const getAppointmentDate = (selectedAppointment, dateFormat = 'dddd, MMMM DD, YYYY', timeFormat = 'h:mm A') => {
    if (!selectedAppointment) {
        return {};
    }
    const appointmentDate = {};
    const rawDate = (0, moment_1.default)(selectedAppointment === null || selectedAppointment === void 0 ? void 0 : selectedAppointment.start_datetime);
    appointmentDate.formattedDate = rawDate.format(dateFormat);
    appointmentDate.formattedTime = rawDate.format(timeFormat);
    return appointmentDate;
};
exports.getAppointmentDate = getAppointmentDate;
/**
 * Gets the patient relationship message based on the purpose of the appointment.
 *
 * @param {Object} appointment.purpose - The purpose of the appointment.
 * @returns {Object} The message object corresponding to the patient relationship.
 */
const getPatientRelationship = ({ purpose }) => {
    if ((purpose === null || purpose === void 0 ? void 0 : purpose.patient_relationship) === Filters_1.NEW_PATIENT) {
        return messages_1.default.newPatient;
    }
    else if ((purpose === null || purpose === void 0 ? void 0 : purpose.patient_relationship) === Filters_1.ESTABLISHED_PATIENT) {
        return messages_1.default.establishedPatient;
    }
};
exports.getPatientRelationship = getPatientRelationship;
/**
 * Gets the patient visit method message based on the purpose of the appointment.
 *
 * @param {Object} appointment.purpose - The purpose of the appointment.
 * @returns {Object} The message object corresponding to the patient visit method.
 */
const getPatientVisitMethod = ({ purpose }) => {
    if ((purpose === null || purpose === void 0 ? void 0 : purpose.visit_method) === VisitTypes_1.IN_PERSON) {
        return messages_1.default.appointmentInPersonTitle;
    }
    else if ((purpose === null || purpose === void 0 ? void 0 : purpose.visit_method) === VisitTypes_1.VIDEO) {
        return messages_1.default.appointmentVideoTitle;
    }
    else if ((purpose === null || purpose === void 0 ? void 0 : purpose.visit_method) === VisitTypes_1.PHONE) {
        return messages_1.default.appointmentPhoneTitle;
    }
};
exports.getPatientVisitMethod = getPatientVisitMethod;
/**
 * Gets whether the current page is embedded in a Cerner application.
 * This is determined by checking if the 'from' URL parameter is set to 'cerner_embedded_order_appts'.
 * @returns {boolean} Returns true if the 'from' URL parameter is 'cerner_embedded_order_appts', otherwise false.
 */
const isOrderBasedScheduling = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const isCernerEmbedded = urlParams.get('from') === 'cerner_embedded_order_appts';
    return isCernerEmbedded;
};
exports.isOrderBasedScheduling = isOrderBasedScheduling;
/**
 * Takes an array of appointment purposes returned from GX/PBV and turns it into an object of
 * appointment purposes grouped by patient relationship like we use elsewhere (e.g. what's passed
 * to the AppointmentInfo component).
 *
 * @param {Array<{patient_relationship: string, purpose: string, visit_method: string}>} purposes - array of appt purpose objects
 * @returns {Object<string, Array<{name: string, visit_method: string}>>} object of purposes grouped by patient rel (like we do elsewhere)
 */
const buildApptPurposesFromGxApptPurposes = (purposes) => {
    if (!purposes || !Array.isArray(purposes) || purposes.length <= 0) {
        return {
            new: [],
            established: []
        };
    }
    // result is { new: [...], established: [...] } (just like purposeOptionsByRel)
    const result = purposes.reduce((acc, curr) => {
        const { patient_relationship, purpose, visit_method } = curr;
        if (!acc[patient_relationship]) {
            acc[patient_relationship] = [];
        }
        acc[patient_relationship].push({
            name: purpose,
            visit_method
        });
        return acc;
    }, {});
    // parity with purposeOptionsByRel, return empty arrays by default and sort alphabetically
    const sortPurposes = (purposes) => purposes.sort((a, b) => a.name.localeCompare(b.name));
    return {
        new: sortPurposes(result.new || []),
        established: sortPurposes(result.established || [])
    };
};
exports.buildApptPurposesFromGxApptPurposes = buildApptPurposesFromGxApptPurposes;
//# sourceMappingURL=utils.js.map